import React from 'react';
import { X, Download, ExternalLink, Phone, User } from 'lucide-react';
import { formatDateTimeMdY } from '@/utils/dateFormat';
import { adminDialogPanel, adminOverlay } from '@/components/admin/adminUi';

function formatCallDate(value) {
  return formatDateTimeMdY(value, '—');
}

function DownloadLink({ href, label }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      download
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Manrope'] bg-white/10 hover:bg-white/15 text-white border border-white/10"
    >
      <Download className="w-3.5 h-3.5" />
      {label}
    </a>
  );
}

export default function OutreachCallDetailModal({ open, call, loading, onClose, onNavigate }) {
  if (!open) return null;

  const recordingUrl = call?.primary_recording_url
    || call?.recording_url
    || call?.user_recording_url
    || call?.bot_recording_url;

  return (
    <div className={adminOverlay()}>
      <div
        className={`${adminDialogPanel('max-w-2xl')} flex flex-col shadow-2xl`}
        data-testid="outreach-call-detail-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-base font-['Unbounded'] font-semibold text-white">
              Outreach Call Details
            </h2>
            {call?.created_at && (
              <p className="text-xs font-['Manrope'] text-white/40 mt-0.5">
                {formatCallDate(call.created_at)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-sm font-['Manrope'] text-white/40 py-8 text-center">Loading call details…</p>
          ) : !call ? (
            <p className="text-sm font-['Manrope'] text-white/40 py-8 text-center">Call not found</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-['Manrope']">
                <div className="bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-white/40 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Customer</p>
                  <p className="text-white">{call.customer_name}</p>
                  {call.customer_phone && (
                    <p className="text-white/50 mt-1 flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {call.customer_phone}
                    </p>
                  )}
                </div>
                <div className="bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-white/40 mb-1">Outcome</p>
                  <p className="text-white">{call.outcome_label || call.outcome}</p>
                  {call.call_status && (
                    <p className="text-white/50 mt-1">Status: {call.call_status}</p>
                  )}
                </div>
                <div className="bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-white/40 mb-1">Duration</p>
                  <p className="text-white">{call.duration_label || '—'}</p>
                </div>
                <div className="bg-white/[0.03] border border-white/10 p-3">
                  <p className="text-white/40 mb-1">Lead Type</p>
                  <p className="text-white">{call.lead_type_label || '—'}</p>
                </div>
              </div>

              <div className="text-xs font-['Manrope'] text-white/40 space-y-2 border-t border-white/10 pt-3">
                {call.lead_id && (
                  <p>
                    Lead:{' '}
                    {call.lead_type === 'service_request' ? (
                      <button
                        type="button"
                        onClick={() => onNavigate?.(`/admin/service-requests/${call.lead_id}`)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        {call.lead_label || call.lead_id}
                      </button>
                    ) : (
                      <span className="text-white/70">{call.lead_label || call.lead_id}</span>
                    )}
                  </p>
                )}
                {call.deal_id && (
                  <p>
                    Deal:{' '}
                    <button
                      type="button"
                      onClick={() => onNavigate?.(`/admin/deals/${call.deal_id}`)}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      {call.deal_label || call.deal_id}
                    </button>
                  </p>
                )}
                {call.vehicle_info && call.vehicle_info !== '—' && (
                  <p>Vehicle: <span className="text-white/70">{call.vehicle_info}</span></p>
                )}
                {call.workflow_run_id && (
                  <p>Workflow Run: <span className="text-white/50 font-mono">{call.workflow_run_id}</span></p>
                )}
                {call.dograh_call_id && (
                  <p>Dograh Call ID: <span className="text-white/50 font-mono">{call.dograh_call_id}</span></p>
                )}
              </div>

              {call.notes && (
                <div className="border-t border-white/10 pt-3">
                  <p className="text-xs font-['Manrope'] text-white/40 mb-1">Notes</p>
                  <p className="text-sm font-['Manrope'] text-white/70 whitespace-pre-wrap">{call.notes}</p>
                </div>
              )}

              {recordingUrl && (
                <div className="border-t border-white/10 pt-3 space-y-2">
                  <p className="text-xs font-['Unbounded'] text-white/80">Recording</p>
                  <audio controls className="w-full" src={recordingUrl}>
                    Your browser does not support audio playback.
                  </audio>
                  <div className="flex flex-wrap gap-2">
                    <DownloadLink href={call.recording_url} label="Download Recording" />
                    <DownloadLink href={call.user_recording_url} label="User Recording" />
                    <DownloadLink href={call.bot_recording_url} label="Bot Recording" />
                  </div>
                </div>
              )}

              {!recordingUrl && (
                <div className="border-t border-white/10 pt-3">
                  <p className="text-xs font-['Manrope'] text-white/40">No recording available for this call.</p>
                </div>
              )}

              <div className="border-t border-white/10 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-['Unbounded'] text-white/80">Transcript</p>
                  {call.transcript_url && (
                    <a
                      href={call.transcript_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-['Manrope'] text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open transcript file
                    </a>
                  )}
                </div>
                {call.transcript ? (
                  <div className="bg-white/[0.03] border border-white/10 p-4 max-h-64 overflow-y-auto">
                    <p className="text-sm font-['Manrope'] text-white/80 whitespace-pre-wrap leading-relaxed">
                      {call.transcript}
                    </p>
                  </div>
                ) : call.transcript_url ? (
                  <p className="text-xs font-['Manrope'] text-white/50">
                    Full transcript is available as a downloadable file.
                  </p>
                ) : (
                  <p className="text-xs font-['Manrope'] text-white/40">No transcript available for this call.</p>
                )}
                {call.transcript_url && (
                  <DownloadLink href={call.transcript_url} label="Download Transcript" />
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/10 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-['Manrope'] text-white/50 hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}